import { BILAG_CATEGORY_LABELS, type BilagCategory } from '@mesterplan/shared-types';
import type {
  EconomicBlockedAction,
  EconomicConfigDto,
  EconomicProductRecommendationDto,
  EconomicRecommendationDto,
  EconomicRecommendedSetupDto,
  EconomicSettingKey,
} from '@mesterplan/api-client';

/** One selectable e-conomic value. Numbers are strings here, the way the inputs hold them. */
export interface Opt {
  value: string;
  label: string;
}

export const BILAG_CATEGORIES: BilagCategory[] = [
  'materials',
  'tools',
  'transport',
  'subcontractor',
  'office',
  'other',
];

export type LineKey = EconomicSettingKey | `category.${BilagCategory}`;

/** The four settings that have to be chosen before anything can be booked (spec §4.1, D1). */
export const REQUIRED_KEYS: EconomicSettingKey[] = ['journal', 'expense', 'bank', 'invoiceProduct'];

export interface FieldDef {
  label: string;
  /** The short name used in the status header and the saved-confirmation. */
  short: string;
  explain: string;
  required: boolean;
  blocks: EconomicBlockedAction[];
}

export const FIELDS: Record<EconomicSettingKey, FieldDef> = {
  journal: {
    label: 'Kassekladde',
    short: 'Kassekladde',
    explain:
      'Den kladde i e-conomic, hvor Mesterplan lægger bilag klar til jer. I (eller jeres bogholder) bogfører dem derfra.',
    required: true,
    blocks: ['bookCreditorInvoice', 'registerPayment'],
  },
  expense: {
    label: 'Udgiftskonto for leverandørfakturaer',
    short: 'Udgiftskonto',
    explain: 'Den konto, udgiften lander på, når I bogfører en faktura fra en leverandør – typisk Varekøb.',
    required: true,
    blocks: ['bookCreditorInvoice'],
  },
  bank: {
    label: 'Bankkonto for indbetalinger',
    short: 'Bankkonto',
    explain: 'Den konto, kundernes betalinger bogføres på – den bank, I får pengene ind på.',
    required: true,
    blocks: ['registerPayment'],
  },
  invoiceProduct: {
    label: 'Standardvare på fakturalinjer',
    short: 'Standardvare',
    explain: 'e-conomic kræver en vare på hver fakturalinje. Linjer uden vare fra Mesterplan får denne.',
    required: true,
    blocks: ['pushInvoice'],
  },
  supplierGroup: {
    label: 'Leverandørgruppe for nye leverandører',
    short: 'Leverandørgruppe',
    explain: 'Bruges, når Mesterplan opretter en ny leverandør i e-conomic fra en e-faktura.',
    required: false,
    blocks: ['createSupplier'],
  },
  paymentTerms: {
    label: 'Betalingsbetingelse for nye leverandører',
    short: 'Betalingsbetingelse',
    explain: 'Hvor lang tid I har til at betale nye leverandører, fx Netto 8 dage.',
    required: false,
    blocks: ['createSupplier'],
  },
};

export const BLOCK_NAMES: Record<EconomicBlockedAction, string> = {
  bookCreditorInvoice: 'Bogfør leverandørfakturaer',
  registerPayment: 'Indbetalinger til e-conomic',
  pushInvoice: 'Send fakturaer med fritekstlinjer',
  createSupplier: 'Opret leverandør i e-conomic',
};

export function isCategoryKey(key: LineKey): key is `category.${BilagCategory}` {
  return key.startsWith('category.');
}

export function fieldDef(key: LineKey): FieldDef {
  if (!isCategoryKey(key)) return FIELDS[key];
  const label = `Konto for ${BILAG_CATEGORY_LABELS[key.slice(9) as BilagCategory].toLowerCase()}`;
  return { label, short: label, explain: 'Tom = udgiften lander på udgiftskontoen.', required: false, blocks: [] };
}

export const LINE_KEYS: LineKey[] = [
  'journal',
  'expense',
  'bank',
  'invoiceProduct',
  'supplierGroup',
  'paymentTerms',
  ...BILAG_CATEGORIES.map((c): LineKey => `category.${c}`),
];

/** What the backend proposes for one line. `value` null = nothing confident, see `candidates`. */
export interface Recommendation {
  value: string | null;
  label: string | null;
  reason: string;
  confidence: EconomicRecommendationDto['confidence'];
  candidates: Opt[];
  complete: boolean;
}

function normalise(rec: EconomicRecommendationDto | EconomicProductRecommendationDto): Recommendation {
  return {
    value: rec.value == null ? null : String(rec.value),
    label: rec.label,
    reason: rec.reason,
    confidence: rec.confidence,
    candidates: rec.candidates.map((c) => ({ value: String(c.value), label: c.label })),
    complete: rec.candidatesComplete,
  };
}

export function recommendations(setup: EconomicRecommendedSetupDto): Partial<Record<LineKey, Recommendation>> {
  const out: Partial<Record<LineKey, Recommendation>> = {
    journal: normalise(setup.journal),
    expense: normalise(setup.supplierExpenseAccount),
    bank: normalise(setup.bankAccount),
    invoiceProduct: normalise(setup.invoiceProduct),
    supplierGroup: normalise(setup.supplierGroup),
    paymentTerms: normalise(setup.paymentTerms),
  };
  for (const c of setup.categoryAccounts) {
    if ((BILAG_CATEGORIES as string[]).includes(c.category)) {
      out[`category.${c.category as BilagCategory}`] = normalise(c.recommendation);
    }
  }
  return out;
}

/** The values stored in EconomicConfig, keyed by line. Unset lines are left out. */
export function storedValues(config: EconomicConfigDto | undefined): Partial<Record<LineKey, string>> {
  if (!config) return {};
  const out: Partial<Record<LineKey, string>> = {};
  const set = (key: LineKey, v: number | string | null | undefined) => {
    if (v != null && String(v).trim() !== '') out[key] = String(v).trim();
  };
  set('journal', config.defaultJournalNumber);
  set('bank', config.defaultContraAccount);
  set('expense', config.defaultSupplierExpenseAccount);
  set('invoiceProduct', config.defaultInvoiceProductNumber);
  set('supplierGroup', config.defaultSupplierGroupNumber);
  set('paymentTerms', config.defaultSupplierPaymentTermsNumber);
  for (const cat of BILAG_CATEGORIES) set(`category.${cat}`, config.categoryAccountMapping?.[cat]);
  return out;
}

export type LineType = 'choose' | 'auto' | 'autoCheck' | 'optional' | 'saved';

/** Spec §4.2. A stored value always wins over a suggestion. */
export function classify(key: LineKey, stored: string | undefined, gone: boolean, rec?: Recommendation): LineType {
  if (stored && !gone) return 'saved';
  const confident = rec?.value != null && rec.confidence !== 'none';
  if (fieldDef(key).required) {
    if (gone || !confident) return 'choose';
    return rec.confidence === 'low' ? 'autoCheck' : 'auto';
  }
  if (isCategoryKey(key) || !confident) return 'optional';
  return rec.confidence === 'low' ? 'autoCheck' : 'auto';
}

export function joinDa(list: string[]): string {
  return list.length < 2 ? list.join('') : `${list.slice(0, -1).join(', ')} og ${list[list.length - 1]}`;
}

export type BlocksByKey = Partial<Record<string, EconomicBlockedAction[]>>;

/** "Bogfør leverandørfakturaer · Indbetalinger til e-conomic": what the given settings stop. */
export function blocksText(keys: EconomicSettingKey[], blocksByKey: BlocksByKey): string {
  const seen: string[] = [];
  for (const k of keys) {
    for (const b of blocksByKey[k] ?? FIELDS[k].blocks) {
      if (!seen.includes(BLOCK_NAMES[b])) seen.push(BLOCK_NAMES[b]);
    }
  }
  return seen.join(' · ');
}

export interface StatusView {
  tone: 'warn' | 'ok' | 'info';
  title: string;
  body: string;
  /** Missing items to link to: the ones only the user can choose, or all when nothing is to choose. */
  chips: EconomicSettingKey[];
}

interface StatusInput {
  /** Required settings that are not stored (or no longer exist in e-conomic). */
  missing: EconomicSettingKey[];
  /** Keys with a pending pick. */
  covered: Set<string>;
  pendingCount: number;
  /** Missing ones we have no suggestion for. */
  choose: Set<string>;
  firstTime: boolean;
  blocksByKey: BlocksByKey;
}

/** Spec §8.1, connected states. Pending picks count live. */
export function buildStatus(i: StatusInput): StatusView {
  if (i.missing.length === 0) {
    return {
      tone: 'ok',
      title: 'Klar til at bogføre',
      body: 'Leverandørfakturaer, indbetalinger og fakturaer kan nu sendes til e-conomic.',
      chips: [],
    };
  }
  const open = i.missing.filter((k) => !i.covered.has(k));
  if (open.length === 0) {
    return {
      tone: 'info',
      title: `${i.pendingCount} valgt – tryk Gem for at blive klar`,
      body: 'Når I har gemt, kan I bogføre.',
      chips: [],
    };
  }
  const choose = open.filter((k) => i.choose.has(k));
  const found = open.filter((k) => !i.choose.has(k)).map((k) => FIELDS[k].short.toLowerCase());
  const parts: string[] = [];
  if (choose.length) parts.push(`${choose.length} skal I vælge selv.`);
  if (found.length) {
    const names = joinDa(found).replace(/^./, (c) => c.toUpperCase());
    parts.push(`${names} har vi fundet – godkend ${found.length === 1 ? 'den' : 'dem'} nedenfor.`);
  }
  parts.push(`Spærrer lige nu: ${blocksText(open, i.blocksByKey)}.`);
  if (i.pendingCount) parts.push(`${i.pendingCount} valgt, ikke gemt.`);
  return {
    tone: 'warn',
    title: `${i.firstTime ? 'e-conomic er forbundet. ' : ''}Mangler ${open.length} ting før I kan bogføre`,
    body: parts.join(' '),
    chips: choose.length ? choose : open,
  };
}
